/**
 * HelpEngineScreen — Admin panel for managing help texts
 *
 * Features:
 *   - View all help entries by form
 *   - Inline edit help text
 *   - AI single-field generation (Wand icon)
 *   - GenerateAll for selected form
 *   - Delete entry
 *   - Force cache refresh
 */
import React, { useState, useEffect, useCallback } from 'react';
import { HelpCircle, Wand2, Trash2, RefreshCw, Search, Loader, CheckCircle, ChevronDown, ArrowLeft } from 'lucide-react';
import { HelpProvider } from '../../context/HelpContext';
import { GenerateAllHelpButton } from '../../components/help-engine/GenerateAllHelpButton';
import { API_BASE as API_URL } from '../../lib/api';

const PROJECT = 'RetailERP';

export default function HelpEngineScreen({ theme, onClose, userRole }) {
  const [entries, setEntries] = useState([]);
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [aiLoading, setAiLoading] = useState({});
  const [editMap, setEditMap] = useState({});
  const [health, setHealth] = useState(null);

  const isDark = theme === 'dark' || document.body.getAttribute('data-theme') === 'dark';
  const bg = isDark ? '#0f172a' : '#f8fafc';
  const surface = isDark ? '#1e293b' : '#ffffff';
  const border = isDark ? '#334155' : '#e2e8f0';
  const text = isDark ? '#e2e8f0' : '#0f172a';
  const muted = isDark ? '#64748b' : '#94a3b8';

  const loadData = useCallback(async (form) => {
    setLoading(true);
    try {
      const url = form
        ? `${API_URL}/api/help/admin/list/${PROJECT}?form=${encodeURIComponent(form)}`
        : `${API_URL}/api/help/admin/list/${PROJECT}`;
      const res = await fetch(url);
      const json = await res.json();
      if (json.success) {
        setEntries(json.data || []);
        if (json.forms) setForms(json.forms);
      }
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  const loadHealth = async () => {
    try {
      const res = await fetch(`${API_URL}/api/help/health`);
      const json = await res.json();
      setHealth(json);
    } catch { /* silent */ }
  };

  useEffect(() => {
    loadData(selectedForm);
    loadHealth();
  }, [selectedForm, loadData]);

  const handleSave = async (entry) => {
    const key = `${entry.FormName}_${entry.ControlId}`;
    const newText = editMap[key] ?? entry.HelpText;
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await fetch(`${API_URL}/api/help/${encodeURIComponent(PROJECT)}/${encodeURIComponent(entry.FormName)}/${encodeURIComponent(entry.ControlId)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ helpText: newText }),
      });
      await loadData(selectedForm);
    } catch { /* silent */ }
    setSaving(s => ({ ...s, [key]: false }));
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete help text for "${entry.ControlId}"?`)) return;
    await fetch(`${API_URL}/api/help/${encodeURIComponent(PROJECT)}/${encodeURIComponent(entry.FormName)}/${encodeURIComponent(entry.ControlId)}`, { method: 'DELETE' });
    await loadData(selectedForm);
  };

  const handleAI = async (entry) => {
    const key = `${entry.FormName}_${entry.ControlId}`;
    setAiLoading(s => ({ ...s, [key]: true }));
    try {
      const res = await fetch(`${API_URL}/api/help/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ controlId: entry.ControlId, controlName: entry.ControlId, formName: entry.FormName, projectName: PROJECT }),
      });
      const json = await res.json();
      if (json.success) setEditMap(m => ({ ...m, [key]: json.helpText }));
    } catch { /* silent */ }
    setAiLoading(s => ({ ...s, [key]: false }));
  };

  const handleRefreshCache = async () => {
    if (!selectedForm) return;
    await fetch(`${API_URL}/api/help/admin/refresh-cache/${PROJECT}/${encodeURIComponent(selectedForm)}`, { method: 'POST' });
    alert(`Cache refreshed for ${selectedForm}`);
  };

  const filtered = entries.filter(e =>
    !search || e.ControlId?.toLowerCase().includes(search.toLowerCase()) || e.HelpText?.toLowerCase().includes(search.toLowerCase())
  );

  const controlsForGenAll = filtered.map(e => ({ controlId: e.ControlId, controlName: e.ControlId }));

  return (
    <HelpProvider projectName={PROJECT} formName={selectedForm} userRole={userRole || 'SUPER_ADMIN'}>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: bg, color: text, fontFamily: "system-ui, -apple-system, sans-serif", fontSize: 13 }} data-dev-component="HelpEngineScreen" data-dev-file="screens/help-engine/HelpEngineScreen.jsx">

        {/* Top bar */}
        <div style={{ padding: '0.6rem 1.25rem', background: surface, borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
          {onClose && (
            <button
              data-testid="help-engine-back-btn"
              onClick={onClose}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', borderRadius: 7, border: 'none', cursor: 'pointer',
                fontSize: 11, fontWeight: 700,
                background: isDark ? '#1e293b' : '#f1f5f9',
                color: isDark ? '#94a3b8' : '#475569',
              }}
            >
              <ArrowLeft size={13} /> Back
            </button>
          )}
          <HelpCircle size={18} color="#6366f1" />
          <div>
            <div style={{ fontWeight: 800, fontSize: '0.9rem' }}>Help Engine</div>
            <div style={{ fontSize: '0.68rem', color: muted }}>AI-powered contextual help text management — Project: {PROJECT}</div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            {health && (
              <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 999, background: health.masterDb ? '#dcfce7' : '#fee2e2', color: health.masterDb ? '#15803d' : '#dc2626', fontWeight: 700 }}>
                DB {health.masterDb ? 'Connected' : 'Offline'}
              </span>
            )}
            {health?.aiEnabled && (
              <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 999, background: '#ede9fe', color: '#7c3aed', fontWeight: 700 }}>
                Gemini AI Ready
              </span>
            )}
          </div>
        </div>

        {/* Filters */}
        <div style={{ padding: '0.65rem 1.25rem', background: surface, borderBottom: `1px solid ${border}`, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
          {/* Form selector */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <select
              value={selectedForm}
              onChange={e => setSelectedForm(e.target.value)}
              style={{ padding: '0.38rem 2rem 0.38rem 0.7rem', border: `1px solid ${border}`, borderRadius: 6, fontSize: 12, background: bg, color: text, cursor: 'pointer', appearance: 'none', outline: 'none', minWidth: 160 }}
              data-testid="help-form-selector"
            >
              <option value="">All Forms ({entries.length} entries)</option>
              {forms.map(f => <option key={f.FormName} value={f.FormName}>{f.FormName} ({f.cnt})</option>)}
            </select>
            <ChevronDown size={12} style={{ position: 'absolute', right: 6, pointerEvents: 'none', color: muted }} />
          </div>

          {/* Search */}
          <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
            <Search size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: muted }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search control ID or text..."
              style={{ width: '100%', paddingLeft: 26, padding: '0.38rem 0.7rem 0.38rem 26px', border: `1px solid ${border}`, borderRadius: 6, fontSize: 12, background: bg, color: text, outline: 'none' }} />
          </div>

          <GenerateAllHelpButton controls={controlsForGenAll} onDone={() => loadData(selectedForm)} />

          {selectedForm && (
            <button onClick={handleRefreshCache} title="Force refresh JSON cache"
              style={{ padding: '0.38rem 0.7rem', border: `1px solid ${border}`, borderRadius: 6, background: 'transparent', cursor: 'pointer', color: muted, display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
              <RefreshCw size={11} /> Cache
            </button>
          )}
        </div>

        {/* Table */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 1.25rem' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: muted }}>
              <Loader size={20} style={{ animation: 'spin 0.8s linear infinite', margin: '0 auto 8px' }} />
              Loading...
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: muted }}>
              <HelpCircle size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
              <div style={{ fontWeight: 700, marginBottom: 4 }}>No help texts yet</div>
              <div style={{ fontSize: 12 }}>Help texts are created when admins hover over <strong>(i)</strong> icons and save text, or via "AI Help All".</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: bg, borderBottom: `2px solid ${border}` }}>
                  {['Form', 'Control ID', 'Help Text', 'Updated', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700, color: muted, textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.04em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((entry, idx) => {
                  const key = `${entry.FormName}_${entry.ControlId}`;
                  const editVal = editMap[key] ?? entry.HelpText ?? '';
                  const changed = editMap[key] !== undefined && editMap[key] !== entry.HelpText;
                  return (
                    <tr key={idx} style={{ borderBottom: `1px solid ${border}`, background: idx % 2 === 0 ? 'transparent' : (isDark ? '#0f172a33' : '#f8fafc55') }}>
                      <td style={{ padding: '5px 10px', color: '#6366f1', fontWeight: 600, whiteSpace: 'nowrap', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{entry.FormName}</td>
                      <td style={{ padding: '5px 10px', fontFamily: 'monospace', color: isDark ? '#7dd3fc' : '#0369a1', whiteSpace: 'nowrap' }}>{entry.ControlId}</td>
                      <td style={{ padding: '5px 10px', minWidth: 200 }}>
                        <textarea
                          value={editVal}
                          onChange={e => setEditMap(m => ({ ...m, [key]: e.target.value }))}
                          rows={2}
                          style={{
                            width: '100%', background: changed ? (isDark ? '#1e3a1e' : '#f0fdf4') : bg,
                            color: text, border: `1px solid ${changed ? '#16a34a' : border}`,
                            borderRadius: 4, padding: '3px 6px', fontSize: 11, resize: 'vertical', outline: 'none', fontFamily: 'inherit',
                          }}
                        />
                      </td>
                      <td style={{ padding: '5px 10px', color: muted, whiteSpace: 'nowrap', fontSize: 10 }}>
                        {entry.UpdatedAt ? new Date(entry.UpdatedAt).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ padding: '5px 10px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {changed && (
                            <button onClick={() => handleSave(entry)} disabled={saving[key]}
                              style={{ padding: '2px 7px', background: '#16a34a', border: 'none', borderRadius: 4, cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', gap: 3, fontSize: 10 }}>
                              {saving[key] ? <Loader size={10} /> : <CheckCircle size={10} />} Save
                            </button>
                          )}
                          <button onClick={() => handleAI(entry)} disabled={!!aiLoading[key]}
                            style={{ padding: '2px 7px', background: '#7c3aed', border: 'none', borderRadius: 4, cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', gap: 3, fontSize: 10 }}>
                            {aiLoading[key] ? <Loader size={10} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Wand2 size={10} />} AI
                          </button>
                          <button onClick={() => handleDelete(entry)}
                            style={{ padding: '2px 6px', background: '#dc2626', border: 'none', borderRadius: 4, cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', fontSize: 10 }}>
                            <Trash2 size={10} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '0.5rem 1.25rem', background: surface, borderTop: `1px solid ${border}`, fontSize: 10, color: muted, display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
          <span>{filtered.length} entries {selectedForm ? `in "${selectedForm}"` : 'across all forms'}</span>
          <span>DB: RC26MASTERDB → AppHelpText • Cache: /public/help-data/</span>
        </div>

        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </HelpProvider>
  );
}
